import { create } from "zustand";

export interface TrawlProgress {
  papersProcessed: number;
  totalPapers: number;
  citationsFound: number;
  currentPaperTitle: string;
  currentOperation: string;
  percent: number;
  statusMessage: string;
}

export interface TrawlOptions {
  maxResults: number;
  includeReferences: boolean;
  includeRecommendations: boolean;
}

interface TrawlState {
  isTrawling: boolean;
  isCompleted: boolean;
  error: string | null;
  progress: TrawlProgress;
  options: TrawlOptions;
  abortController: AbortController | null;

  startTrawl: () => AbortController;
  /** startTrawl unless one is already running (then null) — check-and-set in one synchronous step. */
  tryStartTrawl: () => AbortController | null;
  updateProgress: (update: Partial<TrawlProgress>) => void;
  completeTrawl: (message: string) => void;
  failTrawl: (error: string) => void;
  resetTrawl: () => void;
  setOptions: (options: Partial<TrawlOptions>) => void;
  cancelTrawl: () => void;
}

const initialProgress: TrawlProgress = {
  papersProcessed: 0,
  totalPapers: 0,
  citationsFound: 0,
  currentPaperTitle: "",
  currentOperation: "",
  percent: 0,
  statusMessage: "",
};

const defaultOptions: TrawlOptions = {
  maxResults: 10,
  includeReferences: true,
  includeRecommendations: true,
};

export const useTrawlStore = create<TrawlState>((set, get) => ({
  isTrawling: false,
  isCompleted: false,
  error: null,
  progress: { ...initialProgress },
  options: { ...defaultOptions },
  abortController: null,

  startTrawl: () => {
    const controller = new AbortController();
    set({
      isTrawling: true,
      isCompleted: false,
      error: null,
      progress: { ...initialProgress },
      abortController: controller,
    });
    return controller;
  },

  tryStartTrawl: () => (get().isTrawling ? null : get().startTrawl()),

  updateProgress: (update) =>
    set((state) => ({
      progress: { ...state.progress, ...update },
    })),

  completeTrawl: (message) =>
    set({
      isTrawling: false,
      isCompleted: true,
      progress: { ...get().progress, statusMessage: message, percent: 100 },
      abortController: null,
    }),

  failTrawl: (error) =>
    set({
      isTrawling: false,
      error,
      abortController: null,
    }),

  resetTrawl: () =>
    set({
      isTrawling: false,
      isCompleted: false,
      error: null,
      progress: { ...initialProgress },
      abortController: null,
    }),

  setOptions: (options) =>
    set((state) => ({
      options: { ...state.options, ...options },
    })),

  cancelTrawl: () => {
    const { abortController } = get();
    if (abortController) {
      abortController.abort();
    }
    set({
      isTrawling: false,
      error: "Cancelled by user",
      abortController: null,
    });
  },
}));
